import React, { useState } from "react";
import { Link } from "react-router-dom";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, Pagination, Skeleton, Table, Text } from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";
import type { WorkflowTemplateListItem } from "@performyard/service-contracts";

import { AdminTitle } from "../../../../components/Admin";
import Spacer from "../../../../components/Spacer";
import CreateWorkflowTemplateModal from "./CreateWorkflowTemplateModal";

import { useRbacPrimaryRolePermissions } from "../../../../api/hooks";
import { useWorkflowTemplates } from "../../../../api/hooks/defs/workflow-templates";

import type { WorkflowTemplateSortBy } from "../../../../api/services/defs/workflow-templates";

import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";

const WORKFLOW_TEMPLATES_PAGE_SIZE = 25;

const isLoadingRow = (data: Partial<WorkflowTemplateListItem>) => !data._id;

const WorkflowsTab = () => {
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	// Creating/editing templates is gated on manageAdmin: the template read/write
	// endpoints enforce the same permission, so viewAdmin-only users (billing-admin,
	// overlord) get a read-only listing rather than an editor whose writes 403.
	const { data: rbacPermissions } = useRbacPrimaryRolePermissions("workflows");
	const canManage = rbacPermissions?.includes("workflows:manageAdmin") ?? false;

	const { sort, setSort, pageNumber, setPageNumber } = useInfoPageTemplateQueryState({
		defaultSortName: "name",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
	});

	const { data, isLoading } = useWorkflowTemplates(
		{
			sortBy: sort.name as WorkflowTemplateSortBy,
			sortOrder: sort.direction,
			limit: WORKFLOW_TEMPLATES_PAGE_SIZE,
			skip: (pageNumber - 1) * WORKFLOW_TEMPLATES_PAGE_SIZE,
		},
		{ placeholderData: (previousData) => previousData }
	);

	const workflowTemplates = data?.workflowTemplates ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / WORKFLOW_TEMPLATES_PAGE_SIZE);

	const onSortChanged = (event: SortChangedEvent<WorkflowTemplateListItem>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const columns: PYColumnDef<WorkflowTemplateListItem>[] = [
		{
			colId: "name",
			headerName: "Workflow",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: WorkflowTemplateListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				const name = (
					<Text size="paragraph-3" weight="regular">
						<span className="py-line-clamp-1">{row.name}</span>
					</Text>
				);
				if (!canManage) return name;
				return (
					<Link
						to={`/edit-workflow/${row._id}`}
						className="text-primary-5 no-underline hover:text-primary-7 hover:underline"
					>
						{name}
					</Link>
				);
			},
		},
		{
			colId: "last_update",
			field: "lastUpdate",
			headerName: "Last Update",
			type: "date",
			width: 220,
			sortable: true,
			cellRendererParams: { dateFormat: "MMM d, yyyy h:mm a" },
		},
		{
			colId: "todoCount",
			headerName: "To Dos",
			width: 140,
			sortable: true,
			headerClass: "[&_.ag-header-cell-label]:justify-center",
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
			valueGetter: (params: { data?: WorkflowTemplateListItem }) => params.data?.todoCount ?? 0,
			cellRenderer: (params: { data: WorkflowTemplateListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-6" />;
				return row.todoCount;
			},
		},
	];

	return (
		<div>
			<div className="flex items-center justify-between gap-4">
				<AdminTitle includeSpacer={false}>Workflows</AdminTitle>
				{canManage && (
					<div>
						<ButtonStandard
							color="primary"
							iconLeft={faPlus}
							dangerouslySetClassName="w-auto"
							onClick={() => setIsCreateModalOpen(true)}
						>
							Create New Workflow Template
						</ButtonStandard>
						<Spacer height={2} />
					</div>
				)}
			</div>

			<div>
				<Table<WorkflowTemplateListItem>
					autoHeight
					columnSizing="fit-grid"
					columnDefs={columns}
					rowData={workflowTemplates}
					loading={isLoading}
					getRowId={(params) => params.data._id}
					rowHeight={48}
					defaultColDef={{
						cellStyle: { display: "flex", alignItems: "center" },
						resizable: false,
						sortingOrder: ["asc", "desc"],
					}}
					emptyStateText="No Workflow Templates Found"
					onSortChanged={onSortChanged}
					initialState={{
						sort: {
							sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction }] : [],
						},
					}}
				/>
			</div>

			{pageCount > 1 && (
				<div className="mt-4 flex justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}

			{isCreateModalOpen && <CreateWorkflowTemplateModal onClose={() => setIsCreateModalOpen(false)} />}
		</div>
	);
};

export default WorkflowsTab;
